import React, { useState, useEffect } from "react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { User, Mail, Lock, Save, Loader2, ShieldAlert, Users, UserCog } from "lucide-react";

const AdminProfilePage = () => {
  const { token, role } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingList, setFetchingList] = useState(false);
  
  // States
  const [userList, setUserList] = useState<any[]>([]); // Subordinates ki list
  const [selectedUserId, setSelectedUserId] = useState<string>("me"); // Default "me" (Logged-in user)
  
  const [formData, setFormData] = useState({ 
    name: "", 
    email: "", 
    password: "" 
  });

  const canUpdate = role === "SUPER_ADMIN" || role === "ADMIN";

  // 1. Load Subordinates List (Sirf Admins ke liye)
  useEffect(() => {
    if (canUpdate) {
      fetchSubordinates();
    }
  }, [role]);

  // 2. Load Selected User Data (Jab dropdown change ho)
  useEffect(() => {
    fetchUserData();
  }, [selectedUserId]);

  const fetchSubordinates = async () => {
    setFetchingList(true);
    try {
      // Backend endpoint jo is admin ke subordinates laaye
    //   const res = await fetch(`https://luxuriyahomes.com/api/admin/my-team`, {
      const res = await fetch(`http://localhost:1001/api/admin/my-team`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setUserList(data);
    } catch (err) {
      console.error("Error fetching team:", err);
    } finally {
      setFetchingList(false);
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      // "me" matlab khud ki profile, varna specific ID
      const endpoint = selectedUserId === "me" ? "me" : selectedUserId;
    //   const res = await fetch(`https://luxuriyahomes.com/api/admin/user/${endpoint}`, {
      const res = await fetch(`http://localhost:1001/api/admin/user/${endpoint}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFormData({ name: data.name, email: data.email, password: "" });
      }
    } catch (err) {
      console.error("Error loading user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canUpdate) return;

    setLoading(true);
    try {
      const res = await fetch(`https://luxuriyahomes.com/api/admin/update-profile/${selectedUserId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) alert("Profile Updated Successfully!");
      else alert("Update Failed");
    } catch (err) {
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 bg-[#161616] rounded-2xl border border-zinc-800 max-w-2xl mx-auto mt-10 shadow-2xl">
      
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl text-white font-light flex items-center gap-3">
          <UserCog className="text-amber-500" size={28} /> Profile Management
        </h2>
      </div>

      {/* STEP 1: SELECT USER DROPDOWN (Sirf Admin ko dikhega) */}
      {canUpdate && (
        <div className="mb-10 p-5 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <label className="text-amber-500 text-xs uppercase tracking-widest block mb-3 flex items-center gap-2">
            <Users size={14} /> Select User to Edit
          </label>
          <select 
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full bg-[#0c0c0c] border border-zinc-700 p-3 rounded-lg text-white outline-none focus:border-amber-500"
          >
            <option value="me">My Own Profile (Self)</option>
            {userList.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.role})
              </option>
            ))}
          </select>
          {fetchingList && <p className="text-zinc-500 text-[10px] mt-2 animate-pulse">Loading team members...</p>}
        </div>
      )}

      {/* STEP 2: UPDATE FORM */}
      <form onSubmit={handleUpdate} className="space-y-6 relative">
        {loading && (
          <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
             <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        )}

        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest block mb-2">Full Name</label>
          <input 
            className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 rounded-lg text-white focus:border-amber-500 outline-none"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
          />
        </div>

        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest block mb-2">Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 text-zinc-600" size={18} />
            <input 
              type="email"
              className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-11 rounded-lg text-white focus:border-amber-500 outline-none"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>
        </div>

        <div>
          <label className="text-zinc-500 text-xs uppercase tracking-widest block mb-2">New Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 text-zinc-600" size={18} />
            <input 
              type="password"
              className="w-full bg-[#0c0c0c] border border-zinc-800 p-3 pl-11 rounded-lg text-white focus:border-amber-500 outline-none"
              placeholder="••••••••"
              onChange={(e) => setFormData({...formData, password: e.target.value})}
            />
          </div>
        </div>

        <button 
          className="w-full bg-amber-600 hover:bg-amber-500 text-black font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all mt-4"
          disabled={loading}
        >
          <Save size={20} /> Save Changes
        </button>
      </form>
    </div>
  );
};

export default AdminProfilePage;